<template>
  <BaseModal
    :modalTitle="$t('area.employee.invite.title')"
    :modalIntro="$t('area.employee.invite.intro')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :resetCallback="formResetCallback"
      :onSubmit="onSendInvitesClick">
      <UiInputTextAreaVee
        name="emails"
        :label="$t('form.label.email-addresses')"
        :placeholder="$t('form.placeholder.email-addresses')"
        v-model="emailsText"
        @update:model-value="resetValidationMessages" />

      <button
        v-if="EMU.isEmulatingFirestore"
        type="button"
        class="-mt-2 mb-2 flex w-fit cursor-pointer items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
        @click="addFakeEmail">
        <IconLucideSparkles class="h-4 w-4" />
        {{ $t('W: Tilføj dummy-email') }}
      </button>

      <RoleSelection
        v-model:userMainRole="userMainRole"
        @update:user-main-role="resetValidationMessages" />

      <WorkAreaSelection
        v-model:userWorkAreas="userWorkAreas"
        @update:user-work-areas="resetValidationMessages" />

      <span class="text-sm text-text-primary">
        {{ $t('area.employee.invite.email-count') }}: {{ validEmails.length }}
      </span>

      <InfoText
        :text="validationText"
        class="mt-2 mb-4"
        theme="warning" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.send-invites')"
          type="submit"
          :isDisabled="!isFormValid || isCurrentLabAccountDeactivated"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { generateFakeEmail } from '@/globals/typescript/utils/fakeEmail'
import { EMU } from '@/globals/typescript/utils/host'
import { useToast } from '@/globals/typescript/utils/toast'
import { useUserStore } from '@/stores/userStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import {
  type TLabManagementRole,
  type TLabManagementWorkArea,
} from '@team-milva/lab-models/user'
import { union } from 'lodash-es'
import { mapState } from 'pinia'
import isEmail from 'validator/lib/isEmail'
import type { PropType } from 'vue'

export default {
  name: 'InviteModal',
  emits: ['close'],
  props: {
    onSuccess: {
      type: Function as PropType<() => void>,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      emailsText: '',
      userMainRole: 'user' as TLabManagementRole,
      userWorkAreas: [] as TLabManagementWorkArea[],
      errorText: '',

      isLoading: false,
      showSaveIcon: false,

      EMU,
    }
  },
  computed: {
    ...mapState(useUserStore, ['currentUser']),
    ...mapState(useAccountStore, [
      'allLabAccountUsers',
      'labAccount',
      'isCurrentLabAccountDeactivated',
    ]),
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),
    emailList(): string[] {
      return union(
        this.emailsText
          .split(',')
          .map((email) => email.replace(/\s+/g, ''))
          .filter(
            (email) =>
              email.length > 0 &&
              isEmail(email, { allow_utf8_local_part: true })
          )
      )
    },
    existingEmails(): string[] {
      return this.allLabAccountUsers
        .filter((x) => this.emailList.includes(x.email))
        .map((x) => x.email)
    },
    validEmails(): string[] {
      return this.emailList.filter(
        (email) => !this.existingEmails.includes(email)
      )
    },
    validationText(): string {
      if (this.existingEmails.length) {
        return `${this.$t(
          'area.employee.invite.message.existing-emails'
        )}: <br> ${this.existingEmails.join(', ')}`
      }

      return ''
    },
    isFormValid(): boolean {
      return this.validEmails.length > 0
    },
  },
  methods: {
    addFakeEmail() {
      const email = generateFakeEmail()
      this.emailsText = this.emailsText.trim()
        ? `${this.emailsText.trim()}, ${email}`
        : email
      this.resetValidationMessages()
    },
    async onSendInvitesClick() {
      if (!this.isFormValid) {
        return
      }

      this.resetValidationMessages()
      this.isLoading = true

      try {
        if (!this.currentUser?.id || !this.labAccount?.id) {
          this.errorText = this.$t('error.common.standard')
          return
        }

        // TODO: remove cast after deploying and re-running openapi:gen
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (managementApi as any)('post', '/invites', {
          body: {
            userID: this.currentUser.id,
            accountID: this.labAccount.id,
            emails: this.validEmails,
            mainRole: this.userMainRole,
            workAreas: this.userWorkAreas,
          },
        })

        if (result.success) {
          this.showSaveIcon = true
          setTimeout(() => {
            this.showSaveIcon = false
          }, this.defaultMessageSaveIconTime)
          const count = this.validEmails.length
          const message =
            count === 1
              ? this.$t('W: Invitation sendt')
              : this.$t('W: {count} invitationer sendt', { count })
          this.unsavedGuard.takeSnapshot()
          useToast().success(message)
          this.$emit('close')
          this.onSuccess?.()
          return
        }

        useToast().error(this.$t('W: Kunne ikke sende invitation'))
        this.errorText = this.$t('error.common.standard')
      } finally {
        this.isLoading = false
      }
    },
    formResetCallback(): boolean {
      return !this.errorText
    },
    resetValidationMessages() {
      this.errorText = ''
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({
        emailsText: this.emailsText,
        userMainRole: this.userMainRole,
        userWorkAreas: [...this.userWorkAreas],
      }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

